import React, { useEffect, useState, useCallback } from "react";
import { Link, Outlet, useNavigate, NavLink } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import {
  Briefcase, Users, Building2, LayoutDashboard, LogOut,
  ArrowLeftRight, ShieldCheck, KeyRound,
} from "lucide-react";
import { Toaster } from "sonner";

export default function AgencyLayout() {
  const { org, user, logout, switchOrg } = useAuth();
  const nav = useNavigate();
  const [ownOrgs, setOwnOrgs] = useState([]);
  const [openPw, setOpenPw] = useState(false);

  useEffect(() => {
    api.get("/organizations/mine").then((r) => setOwnOrgs(r.data || [])).catch(() => {});
  }, []);

  // If the current org is NOT an agency, boot back to /app
  useEffect(() => {
    if (org && !org.is_agency) nav("/app/dashboard", { replace: true });
  }, [org, nav]);

  const jumpToOrg = useCallback(async (o) => {
    await switchOrg(o.id);
    nav(o.is_agency ? "/agency/dashboard" : "/app/dashboard");
  }, [switchOrg, nav]);

  if (!org?.is_agency) return null;

  return (
    <div className="min-h-screen bg-background text-foreground flex">
      <Toaster theme="light" position="top-right" richColors />
      {/* Sidebar */}
      <aside className="w-64 border-r border-border bg-card/40 flex flex-col shrink-0" data-testid="agency-sidebar">
        <div className="p-4 border-b border-border">
          <Link to="/agency/dashboard" className="flex items-center gap-2.5" data-testid="agency-logo">
            <img src="/zenith360-logo.png" alt="Zenith 360" className="h-9 w-auto" />
            <div>
              <div className="font-heading font-bold text-sm text-logo-gradient">Zenith 360</div>
              <div className="text-[9px] font-mono uppercase tracking-widest text-foreground/60 leading-tight">Agency Panel</div>
            </div>
          </Link>
        </div>

        <div className="p-3 border-b border-border">
          <div className="text-[9px] font-mono uppercase tracking-widest text-foreground/60 mb-1">Agency</div>
          <div className="flex items-center gap-2">
            <Briefcase className="w-4 h-4 text-accent" />
            <div className="font-medium text-sm truncate">{org.name}</div>
          </div>
        </div>

        <nav className="p-2 space-y-0.5 flex-1">
          {[
            { to: "/agency/dashboard", icon: LayoutDashboard, label: "Dashboard",       test: "agency-nav-dashboard" },
            { to: "/agency/clients",   icon: Building2,       label: "Client companies", test: "agency-nav-clients" },
          ].map((it) => {
            const Icon = it.icon;
            return (
              <NavLink key={it.to} to={it.to} data-testid={it.test}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-3 py-2 rounded-sm text-sm transition-colors ${isActive ? "bg-accent/10 text-accent font-medium" : "text-foreground/75 hover:bg-muted"}`}>
                <Icon className="w-4 h-4" /> {it.label}
              </NavLink>
            );
          })}
        </nav>

        {/* Own orgs quick-switch (jump into own business orgs / other agencies) */}
        {ownOrgs.length > 1 && (
          <div className="p-3 border-t border-border">
            <div className="text-[9px] font-mono uppercase tracking-widest text-foreground/60 mb-2">Your other orgs</div>
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {ownOrgs.filter((o) => o.id !== org.id).slice(0, 6).map((o) => (
                <button key={o.id} onClick={() => jumpToOrg(o)}
                  data-testid={`agency-switch-${o.id}`}
                  className="w-full flex items-center gap-2 px-2 py-1.5 rounded-sm text-xs hover:bg-muted text-left">
                  {o.is_agency ? <Briefcase className="w-3.5 h-3.5 text-accent shrink-0" /> : <Building2 className="w-3.5 h-3.5 text-foreground/60 shrink-0" />}
                  <span className="truncate flex-1">{o.name}</span>
                  <ArrowLeftRight className="w-3 h-3 text-foreground/40" />
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="p-3 border-t border-border flex items-center gap-2">
          <div className="w-8 h-8 rounded-sm bg-accent/15 text-accent flex items-center justify-center text-xs font-semibold">
            {(user?.full_name || "U")[0]?.toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-medium truncate">{user?.full_name}</div>
            <div className="text-[10px] text-foreground/60 truncate">{user?.email}</div>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setOpenPw(true)} data-testid="agency-change-password"
            className="rounded-sm px-2" title="Change password">
            <KeyRound className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={logout} data-testid="agency-logout" className="rounded-sm px-2">
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-y-auto">
        <Outlet />
      </main>
      <ChangePasswordDialog open={openPw} onOpenChange={setOpenPw} />
    </div>
  );
}
